import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Alignment,
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  Font,
  Heading,
  Image,
  ImageCaption,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  LinkImage,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  Strikethrough,
  Table,
  TableToolbar,
  Underline,
} from "ckeditor5";
import faTranslations from "ckeditor5/translations/fa.js";
import { AlertTriangle, ImagePlus, Video } from "lucide-react";
import { cx } from "../lib/cx";
import { LiaImageUploadAdapterPlugin, LiaVideoPlugin } from "./editor-media";

type ClassicEditorInstance = Awaited<ReturnType<typeof ClassicEditor.create>>;

export interface RichTextEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hint?: string;
  error?: string;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
}

/**
 * ویرایشگر محتوای واحد Lia.
 *
 * - React integration رسمی CKEditor 5
 * - آپلود مستقیم تصویر به Backend پروژه + Drag/Drop/Paste
 * - آپلود مستقیم MP4/WebM/Ogg به Backend و ذخیره به صورت <video>
 * - Embed ویدئوهای YouTube/Vimeo و سایر Providerهای پشتیبانی‌شده
 * - Insert image via URL
 * - سازگار با React Hook Form و Light/Dark theme
 */
export function RichTextEditor({
  label,
  value,
  onChange,
  onBlur,
  hint,
  error,
  placeholder = "متن کامل را اینجا بنویسید…",
  minHeight = 320,
  disabled = false,
}: RichTextEditorProps) {
  const editorRef = useRef<ClassicEditorInstance | null>(null);
  const latestValueRef = useRef(value ?? "");
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);

  latestValueRef.current = value ?? "";

  const config = useMemo(() => ({
    licenseKey: import.meta.env.VITE_CKEDITOR_LICENSE_KEY?.trim() || "GPL",
    plugins: [
      Essentials,
      Paragraph,
      Heading,
      Bold,
      Italic,
      Underline,
      Strikethrough,
      Link,
      List,
      BlockQuote,
      Alignment,
      Font,
      RemoveFormat,
      PasteFromOffice,
      Table,
      TableToolbar,
      Image,
      ImageCaption,
      ImageStyle,
      ImageToolbar,
      ImageResize,
      ImageUpload,
      ImageInsert,
      ImageInsertViaUrl,
      LinkImage,
      MediaEmbed,
      LiaImageUploadAdapterPlugin,
      LiaVideoPlugin,
    ],
    toolbar: {
      items: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "removeFormat",
        "|",
        "fontSize",
        "fontFamily",
        "fontColor",
        "fontBackgroundColor",
        "|",
        "alignment",
        "|",
        "bulletedList",
        "numberedList",
        "|",
        "link",
        "insertImage",
        "uploadVideo",
        "mediaEmbed",
        "blockQuote",
        "insertTable",
      ],
      shouldNotGroupWhenFull: false,
    },
    image: {
      insert: {
        integrations: ["upload", "url"],
      },
      toolbar: [
        "imageStyle:inline",
        "imageStyle:block",
        "imageStyle:side",
        "|",
        "toggleImageCaption",
        "imageTextAlternative",
        "|",
        "resizeImage",
        "linkImage",
      ],
      resizeOptions: [
        { name: "resizeImage:original", value: null, label: "اندازه اصلی" },
        { name: "resizeImage:75", value: "75", label: "۷۵٪" },
        { name: "resizeImage:50", value: "50", label: "۵۰٪" },
        { name: "resizeImage:25", value: "25", label: "۲۵٪" },
      ],
    },
    mediaEmbed: {
      previewsInData: true,
    },
    table: {
      contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
    },
    language: "fa",
    translations: [faTranslations],
    root: {
      placeholder,
    },
  }), [placeholder]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const nextValue = value ?? "";
    if (editor.getData() !== nextValue) editor.setData(nextValue);
  }, [value]);

  useEffect(() => {
    const handleVideoUploadError = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      setMediaError(detail || "آپلود ویدئو انجام نشد.");
    };

    window.addEventListener("lia:ckeditor-video-error", handleVideoUploadError);
    return () => window.removeEventListener("lia:ckeditor-video-error", handleVideoUploadError);
  }, []);

  const fallback = runtimeError !== null;

  return (
    <div
      className={cx("field rich-editor", error && "is-invalid", disabled && "is-disabled", fallback && "rich-editor--fallback")}
      style={{ "--editor-min-height": `${minHeight}px` } as CSSProperties}
    >
      <div className="rich-editor__label-row">
        <span className="field__label">{label}</span>
        {!fallback ? (
          <span className="rich-editor__capabilities" aria-label="امکانات رسانه‌ای ویرایشگر">
            <span><ImagePlus size={14} /> تصویر</span>
            <span><Video size={14} /> ویدئو</span>
          </span>
        ) : null}
      </div>

      {mediaError ? (
        <div className="rich-editor__error" role="alert">
          <AlertTriangle size={18} />
          <span>{mediaError}</span>
          <button type="button" onClick={() => setMediaError(null)}>بستن</button>
        </div>
      ) : null}

      {fallback ? (
        <>
          <div className="rich-editor__error" role="alert">
            <AlertTriangle size={18} />
            <span>ویرایشگر پیشرفته بارگذاری نشد. برای جلوگیری از از دست رفتن محتوا، حالت متنی فعال شده است.</span>
          </div>
          <textarea
            className="rich-editor__fallback-textarea"
            dir="rtl"
            disabled={disabled}
            value={value ?? ""}
            onChange={(event) => onChange(event.target.value)}
            onBlur={onBlur}
            style={{ minHeight }}
          />
          {import.meta.env.DEV ? <small className="field__hint" dir="ltr">{runtimeError}</small> : null}
        </>
      ) : (
        <div className="rich-editor__shell" dir="rtl">
          <CKEditor
            editor={ClassicEditor}
            data={value ?? ""}
            config={config}
            disabled={disabled}
            onReady={(editor) => {
              editorRef.current = editor as ClassicEditorInstance;
              const expectedValue = latestValueRef.current;
              if (editor.getData() !== expectedValue) editor.setData(expectedValue);
              setRuntimeError(null);
              setMediaError(null);
            }}
            onAfterDestroy={() => {
              editorRef.current = null;
            }}
            onChange={(_, editor) => {
              const nextValue = editor.getData();
              if (nextValue === latestValueRef.current) return;
              latestValueRef.current = nextValue;
              onChange(nextValue);
            }}
            onBlur={() => onBlur?.()}
            onError={(editorError, details) => {
              if (!details.willEditorRestart) {
                editorRef.current = null;
                setRuntimeError(editorError instanceof Error ? editorError.message : "CKEditor runtime error");
              }
            }}
          />
        </div>
      )}

      {error ? (
        <small className="field__error">{error}</small>
      ) : hint ? (
        <small className="field__hint">{hint}</small>
      ) : null}
    </div>
  );
}
